// INSTAGRAM ID BACKFILL — the PURE core (leaf; no I/O, no imports).
//
// Brief: _architect/briefs/2026-09-28-instagram-id-backfill-tool.md
//
// ⚠️⚠️ THIS IS AN EXTRACTION, NOT A REWRITE. Every function below was lifted
// from `scripts/backfill-instagram-account-id.ts`, which has run against
// production repeatedly. The script, the server module and the browser page
// all build their plan through THESE functions, so the CLI's dry run and the
// UI's dry run cannot disagree about an account. If you change a rule here,
// you change it for all three — which is the point.
//
// It lives in src/lib (not server/) because the browser ASSEMBLES the plan
// from chunked Graph results: the server only runs the Graph calls, the
// classification is this file's, and the client and the script call it.
//
// WHERE THE DATA LIVES (investigated 2026-06-23, carried over from the script):
//   - The numeric Facebook PAGE ID is NOT a field on the Account. It lives on
//     the linked Zernio profile: account.zernioProfileId -> zernioProfiles/{id}
//     .facebookAccount.metadata.selectedPageId. No profile / no FB account / no
//     selectedPageId = cannot be backfilled, and each has its OWN pageNote.
//   - The profile ALSO caches an IG id at instagramAccount.metadata
//     .instagramScopedId — surfaced as the `zernioIgId` cross-check column.

export type AccountRow = {
  id: string;
  name: string;
  zernioProfileId: string | null;
  currentIgId: string | null;
  currentIgName: string | null;
  // resolved from the linked Zernio profile:
  pageId: string | null;
  pageName: string | null;
  zernioIgId: string | null; // cross-check: profile's cached IG scoped id
  pageNote: string | null; // why pageId is null, when it is
};

export type ProfileLite = {
  selectedPageId: string | null;
  selectedPageName: string | null;
  instagramScopedId: string | null;
  hasFacebookAccount: boolean;
};

/** A Graph result for one Page. `no-ig` (field ABSENT on a 200) is a normal outcome, not an error. */
export type IgFetch =
  | { kind: 'linked'; igId: string; igUsername: string | null }
  | { kind: 'no-ig' }
  | { kind: 'error'; message: string };

// ⚠️ THE SIX ACTIONS ARE THE UI VOCABULARY. Unchanged from the script, strings
// included — the CSV the script writes and the CSV the page downloads must be
// diffable line for line.
export type Action =
  | 'would set'
  | 'already set (matches)'
  | 'already set (DIFFERS!)'
  | 'no page id'
  | 'no IG linked'
  | 'error';

export const ACTIONS: readonly Action[] = [
  'would set',
  'already set (matches)',
  'already set (DIFFERS!)',
  'no page id',
  'no IG linked',
  'error',
];

export type PlanRow = AccountRow & {
  fetchedIgId: string | null;
  fetchedIgUsername: string | null;
  action: Action;
  detail: string | null;
};

export function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

/** loadProfiles' per-doc body. `data` is a raw zernioProfiles doc. */
export function profileLiteFromDoc(data: unknown): ProfileLite {
  const d = (data ?? {}) as Record<string, any>;
  const fb = (d.facebookAccount ?? null) as Record<string, any> | null;
  const ig = (d.instagramAccount ?? null) as Record<string, any> | null;
  return {
    selectedPageId: str(fb?.metadata?.selectedPageId),
    selectedPageName: str(fb?.metadata?.selectedPageName),
    instagramScopedId: str(ig?.metadata?.instagramScopedId),
    hasFacebookAccount: Boolean(fb),
  };
}

/**
 * loadAccounts' per-doc body. `data` is a raw accounts doc (or anything with
 * the same fields — the accounts module's StoredAccount spreads the raw doc).
 *
 * ⚠️ The FOUR pageNote strings are the useful part of the "can't do these"
 * list. Do not collapse them into one message.
 */
export function accountRowFromDoc(
  id: string,
  data: unknown,
  profiles: ReadonlyMap<string, ProfileLite>,
): AccountRow {
  const d = (data ?? {}) as Record<string, any>;
  const zernioProfileId = str(d.zernioProfileId);

  let pageId: string | null = null;
  let pageName: string | null = null;
  let zernioIgId: string | null = null;
  let pageNote: string | null = null;

  if (!zernioProfileId) {
    pageNote = 'no zernioProfileId on account';
  } else {
    const profile = profiles.get(zernioProfileId);
    if (!profile) {
      pageNote = `zernioProfileId ${zernioProfileId} has no cached profile doc`;
    } else if (!profile.hasFacebookAccount) {
      pageNote = 'profile has no connected Facebook account';
    } else if (!profile.selectedPageId) {
      pageNote = 'profile Facebook account has no selectedPageId';
    } else {
      pageId = profile.selectedPageId;
      pageName = profile.selectedPageName;
    }
    zernioIgId = profile?.instagramScopedId ?? null;
  }

  return {
    id,
    name: str(d.name) ?? '(unnamed)',
    zernioProfileId,
    currentIgId: str(d.defaultInstagramAccountId),
    currentIgName: str(d.defaultInstagramAccountName),
    pageId,
    pageName,
    zernioIgId,
    pageNote,
  };
}

/**
 * ⚠️ Firestore document order. The script read `collection('accounts').get()`,
 * which returns docs ordered by id — and `--limit` means "the first N
 * page-bearing accounts" IN THAT ORDER. The accounts module's `listAccounts`
 * sorts for the tree instead, so the loader re-sorts here. Plain code-unit
 * comparison, which is Firestore's order for the ASCII auto-ids we use.
 */
export function sortByDocId<T extends { id: string }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export function classify(row: AccountRow, fetch: IgFetch): Omit<PlanRow, keyof AccountRow> {
  if (fetch.kind === 'error') {
    return { fetchedIgId: null, fetchedIgUsername: null, action: 'error', detail: fetch.message };
  }
  if (fetch.kind === 'no-ig') {
    return { fetchedIgId: null, fetchedIgUsername: null, action: 'no IG linked', detail: null };
  }
  // linked
  const fetchedIgId = fetch.igId;
  const fetchedIgUsername = fetch.igUsername;
  if (!row.currentIgId) {
    return { fetchedIgId, fetchedIgUsername, action: 'would set', detail: null };
  }
  if (row.currentIgId === fetchedIgId) {
    return { fetchedIgId, fetchedIgUsername, action: 'already set (matches)', detail: null };
  }
  return {
    fetchedIgId,
    fetchedIgUsername,
    action: 'already set (DIFFERS!)',
    detail: `current=${row.currentIgId} fetched=${fetchedIgId}`,
  };
}

/** ⚠️ The stored name is the `@username` form. */
export function igNameFromUsername(username: string | null): string | null {
  if (!username) return null;
  return username.startsWith('@') ? username : `@${username}`;
}

/**
 * The page ids a run must query, UNIQUE and in first-seen order. Group
 * operators share a Page, so a fleet-wide run queries each Page once.
 * `limit` caps the page-bearing ACCOUNTS considered (the script's --limit).
 */
export function pageIdsToQuery(accounts: readonly AccountRow[], limit?: number): string[] {
  const withPage = accounts.filter((a) => a.pageId);
  const targets = limit != null ? withPage.slice(0, limit) : withPage;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of targets) {
    const p = a.pageId!;
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

// Stable sort: actionable first, then by name.
export const ACTION_ORDER: Record<Action, number> = {
  'would set': 0,
  'already set (DIFFERS!)': 1,
  'already set (matches)': 2,
  'no IG linked': 3,
  error: 4,
  'no page id': 5,
};

export type BuiltPlan = {
  rows: PlanRow[];
  /** Page-bearing accounts whose Page has not been queried yet (a UI run in progress). */
  pendingAccountIds: string[];
};

/**
 * The script's planning loop, minus the I/O. `accounts` must already be in doc
 * order (see `sortByDocId`) for `limit` to mean what the script means.
 *
 * A target whose Page has no result yet is PENDING, not an error — a browser
 * run assembles the plan while chunks are still arriving. The script always
 * passes a complete map, so it never sees one.
 */
export function buildPlan(
  accounts: readonly AccountRow[],
  results: ReadonlyMap<string, IgFetch>,
  limit?: number,
): BuiltPlan {
  const withPage = accounts.filter((a) => a.pageId);
  const withoutPage = accounts.filter((a) => !a.pageId);
  const targets = limit != null ? withPage.slice(0, limit) : withPage;

  const rows: PlanRow[] = [];
  const pendingAccountIds: string[] = [];

  // Accounts with no page id never hit Graph.
  for (const a of withoutPage) {
    rows.push({ ...a, fetchedIgId: null, fetchedIgUsername: null, action: 'no page id', detail: a.pageNote });
  }
  for (const a of targets) {
    const result = results.get(a.pageId!);
    if (!result) {
      pendingAccountIds.push(a.id);
      continue;
    }
    rows.push({ ...a, ...classify(a, result) });
  }
  // Page-bearing accounts skipped by --limit: reported as untouched.
  if (limit != null) {
    for (const a of withPage.slice(limit)) {
      rows.push({ ...a, fetchedIgId: null, fetchedIgUsername: null, action: 'error', detail: 'skipped by --limit' });
    }
  }

  rows.sort((a, b) => ACTION_ORDER[a.action] - ACTION_ORDER[b.action] || a.name.localeCompare(b.name));
  return { rows, pendingAccountIds };
}

export function countActions(rows: readonly PlanRow[]): Record<Action, number> {
  const counts = Object.fromEntries(ACTIONS.map((a) => [a, 0])) as Record<Action, number>;
  for (const r of rows) counts[r.action]++;
  return counts;
}

// ---------------------------------------------------------------------------
// The write decision
// ---------------------------------------------------------------------------

export type WriteMode = 'set' | 'name-only' | 'skip';

/**
 * ⚠️⚠️ A `DIFFERS` ROW IS WRITTEN ONLY WITH `overwrite`. For the script that is
 * the `--overwrite` flag applied to every row; for the page it is a PER-ROW
 * tick. The same function serves both, and the accounts-module writer re-checks
 * the condition against the LIVE document inside its transaction, so a request
 * that mislabels a DIFFERS row as `would set` is refused there too.
 */
export function shouldWrite(row: PlanRow, overwrite: boolean): WriteMode {
  if (row.action === 'would set') return 'set';
  if (row.action === 'already set (DIFFERS!)') return overwrite ? 'set' : 'skip';
  if (row.action === 'already set (matches)') {
    // Top up a missing display name without touching the (matching) id.
    const wanted = igNameFromUsername(row.fetchedIgUsername);
    return !row.currentIgName && wanted ? 'name-only' : 'skip';
  }
  return 'skip';
}

// ---------------------------------------------------------------------------
// CSV — identical columns to the script's, so the two outputs can be diffed
// ---------------------------------------------------------------------------

export const PLAN_CSV_HEADER = [
  'accountId',
  'accountName',
  'pageId',
  'pageName',
  'currentDefaultIg',
  'fetchedIg',
  'fetchedUsername',
  'zernioIg',
  'action',
  'detail',
] as const;

function csvCell(v: string | null): string {
  const s = v ?? '';
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function planToCsv(rows: readonly PlanRow[]): string {
  const lines = rows.map((r) =>
    [
      r.id,
      r.name,
      r.pageId,
      r.pageName,
      r.currentIgId,
      r.fetchedIgId,
      r.fetchedIgUsername,
      r.zernioIgId,
      r.action,
      r.detail ?? r.pageNote,
    ]
      .map((c) => csvCell(c))
      .join(','),
  );
  return [PLAN_CSV_HEADER.join(','), ...lines].join('\n') + '\n';
}
